import type { ChangeEvent } from 'react'
import type { SectionRow, SettingsRow } from '@/types'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'

import {
    Heart,
    Pause,
    Play,
    Quote,
    Square
} from 'lucide-react'

import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface SongSectionProps {
    section?: SectionRow
    settings: SettingsRow
}

const YOUTUBE_ID = /^[\w-]{6,20}$/

/** Reads the video id out of any common YouTube link. */
const parseYouTubeId = (value: string | null): string | null => {
    if (!value) return null

    try {
        const url = new URL(value.trim())
        const host = url.hostname.replace(/^(www|m|music)\./, '')
        let id: string | null = null

        if (host === 'youtu.be') id = url.pathname.split('/')[1] ?? null
        else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
            if (url.pathname === '/watch') id = url.searchParams.get('v')
            else id = url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/)?.[1] ?? null
        }

        return id && YOUTUBE_ID.test(id) ? id : null
    }
    catch {
        return null
    }
}

const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

    const whole = Math.floor(seconds)

    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

export default function SongSection({ section, settings }: SongSectionProps) {
    const audioRef = useRef<HTMLAudioElement>(null)
    const [playing, setPlaying] = useState(false)
    const [showVideo, setShowVideo] = useState(false)
    const [position, setPosition] = useState(0)
    const [duration, setDuration] = useState(0)
    const [failed, setFailed] = useState(false)

    const file = settings.song_file
    const youtubeId = file ? null : parseYouTubeId(settings.song_youtube_url)
    const spinning = playing || showVideo

    const togglePlay = async () => {
        if (file) {
            const audio = audioRef.current

            if (!audio) return

            if (!audio.paused) {
                audio.pause()

                return
            }

            try {
                await audio.play()
                setFailed(false)
            }
            catch {
                setFailed(true)
            }

            return
        }

        if (youtubeId) setShowVideo(value => !value)
    }

    const seek = (event: ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current
        const next = Number(event.target.value)

        if (!audio || !Number.isFinite(next)) return

        audio.currentTime = next
        setPosition(next)
    }

    const canPlay = Boolean(file || youtubeId)

    return (
        <section id='song' className='relative overflow-hidden bg-plum py-24 text-cream sm:py-32'>
            <div aria-hidden className='absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#6b1d43_0%,#2e1028_55%,#1a0916_100%)]' />
            <div aria-hidden className='star-dust absolute inset-0 opacity-50' />

            <div className='relative mx-auto max-w-6xl px-6 lg:px-10'>
                <SectionHeading section={section} tone='dark' />

                <Reveal className='mx-auto mt-14 max-w-5xl sm:mt-16'>
                    <div className='grid items-center gap-12 rounded-[2.5rem] bg-white/[0.05] p-7 ring-1 ring-white/10 backdrop-blur-sm sm:p-10 md:grid-cols-[auto_1fr] md:gap-14 lg:p-14'>
                        <div className='relative mx-auto size-60 sm:size-72 lg:size-80'>
                            <div
                                aria-hidden
                                className={cn(
                                    'vinyl-grooves absolute inset-0 animate-vinyl rounded-full shadow-[0_30px_70px_-20px_rgba(0,0,0,0.85)] ring-1 ring-white/10 motion-reduce:animate-none',
                                    !spinning && '[animation-play-state:paused]'
                                )}
                            >
                                <div className='absolute inset-[31%] grid place-items-center rounded-full bg-gradient-to-br from-petal via-petal-deep to-wine ring-4 ring-black/40'>
                                    <Heart className='size-7 fill-cream/90 text-cream/90 sm:size-8' />
                                    <span className='absolute top-[18%] h-1 w-1/3 rounded-full bg-cream/40' />
                                    <span className='absolute bottom-[18%] h-1 w-1/4 rounded-full bg-cream/30' />
                                </div>

                                <span className='absolute top-1/2 left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-night ring-2 ring-white/20' />
                            </div>

                            <div aria-hidden className='pointer-events-none absolute inset-0 rounded-full bg-[conic-gradient(from_200deg,transparent_0deg,rgba(255,255,255,0.10)_30deg,transparent_70deg,transparent_180deg,rgba(255,255,255,0.07)_215deg,transparent_250deg)]' />

                            <div
                                aria-hidden
                                className={cn(
                                    'absolute -top-3 right-0 h-44 w-2.5 origin-top rounded-full bg-gradient-to-b from-[#ece6ea] to-[#8f878d] shadow-lg transition-transform duration-700 ease-out sm:h-52 lg:h-56',
                                    spinning ? 'rotate-[24deg]' : 'rotate-[6deg]'
                                )}
                            >
                                <span className='absolute -top-3 left-1/2 size-8 -translate-x-1/2 rounded-full bg-gradient-to-br from-[#e6e0e4] to-[#9d959b] ring-4 ring-plum' />
                                <span className='absolute -bottom-3 left-1/2 h-7 w-5 -translate-x-1/2 rounded-sm bg-[#e8e4e7] shadow' />
                            </div>
                        </div>

                        <div className='text-center md:text-left'>
                            {settings.song_title && (
                                <h3
                                    data-mdlfy-cms={`settings:${settings.id}:song_title`}
                                    className='font-heading text-4xl font-semibold text-cream sm:text-5xl'
                                >
                                    {settings.song_title}
                                </h3>
                            )}

                            {settings.song_artist && (
                                <p
                                    data-mdlfy-cms={`settings:${settings.id}:song_artist`}
                                    className='mt-2 text-lg font-semibold text-[#ff9ab4]'
                                >
                                    {settings.song_artist}
                                </p>
                            )}

                            {settings.song_memory && (
                                <div className='relative mt-7 rounded-3xl bg-night/40 p-6 text-left ring-1 ring-white/10'>
                                    <Quote aria-hidden className='absolute -top-3 left-6 size-7 rounded-full bg-petal p-1.5 text-white' />

                                    <p
                                        data-mdlfy-cms={`settings:${settings.id}:song_memory`}
                                        className='font-heading text-lg leading-relaxed text-cream/90 italic sm:text-xl'
                                    >
                                        {settings.song_memory}
                                    </p>
                                </div>
                            )}

                            {canPlay && (
                                <div className='mt-8 flex flex-col items-center gap-4 md:items-start'>
                                    <button
                                        type='button'
                                        onClick={() => void togglePlay()}
                                        aria-pressed={spinning}
                                        className='inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-petal to-petal-deep py-3.5 pr-7 pl-3.5 text-base font-bold text-white shadow-[0_14px_40px_-12px_rgba(232,84,124,0.9)] transition outline-none hover:brightness-110 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/60 active:scale-95'
                                    >
                                        <span className='grid size-10 place-items-center rounded-full bg-white/20'>
                                            {file && playing && <Pause aria-hidden className='size-5 fill-current' />}
                                            {file && !playing && <Play aria-hidden className='size-5 translate-x-px fill-current' />}
                                            {!file && showVideo && <Square aria-hidden className='size-4 fill-current' />}
                                            {!file && !showVideo && <Play aria-hidden className='size-5 translate-x-px fill-current' />}
                                        </span>

                                        {file
                                            ? (playing ? 'Pause karo' : 'Gaana chalao')
                                            : (showVideo ? 'Gaana band karo' : 'Gaana chalao')}
                                    </button>

                                    {failed && (
                                        <p role='alert' className='text-sm text-[#ffb3c6]'>
                                            Gaana abhi chal nahi pa raha. Ek baar phir try karo.
                                        </p>
                                    )}
                                </div>
                            )}

                            {file && (
                                <div className='mt-6'>
                                    <audio
                                        ref={audioRef}
                                        src={file}
                                        preload='metadata'
                                        onPlay={() => setPlaying(true)}
                                        onPause={() => setPlaying(false)}
                                        onEnded={() => setPlaying(false)}
                                        onTimeUpdate={event => setPosition(event.currentTarget.currentTime)}
                                        onLoadedMetadata={event => setDuration(event.currentTarget.duration)}
                                        onError={() => setFailed(true)}
                                    />

                                    <label htmlFor='song-progress' className='sr-only'>
                                        Gaane ki position
                                    </label>

                                    <input
                                        id='song-progress'
                                        type='range'
                                        min={0}
                                        max={duration || 0}
                                        step={0.5}
                                        value={Math.min(position, duration || 0)}
                                        onChange={seek}
                                        className='h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-petal'
                                    />

                                    <div className='mt-2 flex justify-between text-xs font-semibold text-cream/60 tabular-nums'>
                                        <span>{formatTime(position)}</span>
                                        <span>{formatTime(duration)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {youtubeId && showVideo && (
                        <div className='mx-auto mt-8 max-w-3xl animate-in overflow-hidden rounded-3xl bg-black shadow-2xl ring-1 ring-white/15 duration-500 fade-in zoom-in-95'>
                            <iframe
                                src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&playsinline=1`}
                                title={settings.song_title ? `${settings.song_title} video` : 'Hamara gaana'}
                                allow='autoplay; encrypted-media; picture-in-picture; fullscreen'
                                allowFullScreen
                                referrerPolicy='strict-origin-when-cross-origin'
                                className='aspect-video w-full'
                            />
                        </div>
                    )}
                </Reveal>
            </div>
        </section>
    )
}
